import { useState } from 'react';
// @mui
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
// components
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useUserContext } from 'src/auth/useUserContext';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { Roles } from 'src/types/User';
import { GetUserOrganizationsDto, OrganizationInUserDto } from 'woofwoof-api';
import Iconify from '../../../components/iconify';
import MenuPopover from '../../../components/menu-popover';
import { translateKey } from 'src/locales/translate';


export default function OrganizationPopover() {
  const navigate = useNavigate();
  const theme = useTheme();
  const {
    organizations,
    selectedOrganization,
    setSelectedOrganization,
    isLoading,
  } = useOrganizationContext();

  const { data: userData } = useUserContext();
  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleSelectRole = (
    organization: GetUserOrganizationsDto,
    role: Roles,
  ) => {
    // Always select the organization and role when clicking a role chip
    setSelectedOrganization(organization, role);
    handleClosePopover();
  };

  const handleGoToOrganizationSettings = (
    organization: GetUserOrganizationsDto,
  ) => {
    // Only change role to organization_admin when switching to a different organization
    const isDifferentOrganization =
      selectedOrganization?.id !== organization.id;

    if (isDifferentOrganization) {
      const availableRoles = getUserAvailableRoles(organization.id);
      const defaultRole = availableRoles.includes(Roles.organization_admin)
        ? Roles.organization_admin
        : availableRoles[0]; // Fallback to first available role

      setSelectedOrganization(organization, defaultRole);
    }

    navigate(PATH_DASHBOARD.general.organization.details(organization.id));
    handleClosePopover();
  };

  const getUserAvailableRoles = (organizationId: string): Roles[] => {
    if (!userData?.organizations) return [];

    const userOrg = userData.organizations.find(
      (org: OrganizationInUserDto) => org.organization === organizationId,
    );

    if (!userOrg) return [];

    const userRoles = userOrg.roles;
    if (!userRoles || userRoles.length === 0) return [];

    const supportedRoles: Roles[] = [
      Roles.organization_owner,
      Roles.organization_admin,
      Roles.professional,
    ];

    return supportedRoles.filter((role) => userRoles.includes(role as any));
  };

  const getRoleDisplayName = (role: Roles): string => {
    switch (role) {
      case Roles.organization_admin:
        return translateKey("systemPages.roleAdminLabel");
      case Roles.organization_owner:
        return translateKey("common.owner");
      case Roles.professional:
        return translateKey("search.book.cardSpecialist");
      default:
        return role;
    }
  };

  if (isLoading) {
    return null;
  }

  return (
    <>
      <Button
        color="inherit"
        variant="outlined"
        onClick={handleOpenPopover}
        sx={{
          p: 1,
          ...(openPopover && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '8px',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {selectedOrganization?.logoUrl ? (
            <Avatar
              src={selectedOrganization.logoUrl}
              alt={selectedOrganization.name}
              sx={{ width: theme.spacing(3), height: theme.spacing(3) }}
            />
          ) : (
            <Iconify icon="solar:buildings-2-bold-duotone" width={20} height={20} />
          )}
          <Typography
            variant="body2"
            sx={{
              maxWidth: { xs: 100, sm: 180, md: 300 },
              display: { sm: 'block' },
            }}
            noWrap
          >
            {selectedOrganization?.name || translateKey("hardcoded.wybierz_placowke")}
          </Typography>
          <Iconify icon="eva:chevron-down-fill" width={16} height={16} />
        </Stack>
      </Button>

      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        sx={{
          width: { xs: 'calc(100vw - 32px)', sm: 350 },
          maxWidth: 350,
          p: 0,
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>{translateKey("hardcoded.moje_placowki")}</Typography>
        </Box>

        <Stack sx={{ p: 1, gap: 1 }}>
          {organizations.map((organization) => {
            const isSelected = selectedOrganization?.id === organization.id;
            const availableRoles = getUserAvailableRoles(organization.id);

            const hasOrganizationOwnerRole = availableRoles.includes(
              Roles.organization_owner,
            );

            return (
              <MenuItem
                disableRipple
                key={organization.id}
                onClick={
                  organization.isVerified === false
                    ? () => {
                        const defaultRole =
                          availableRoles[0] || Roles.organization_admin;
                        setSelectedOrganization(organization, defaultRole);
                        handleClosePopover();
                      }
                    : undefined
                }
                sx={{
                  p: 1,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  '&:hover': {
                    bgcolor: 'primary.lighter',
                  },
                  ...(isSelected && {
                    bgcolor: 'primary.lighter',
                  }),
                }}
              >
                <Stack gap={2} sx={{ width: '100%' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar
                      src={organization.logoUrl}
                      sx={{ width: theme.spacing(5), height: theme.spacing(5) }}
                    />
                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {organization.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary' }}
                      >
                        {`${organization.street}, ${organization.city}`}
                      </Typography>
                    </Stack>
                  </Stack>

                  {organization.isVerified === false ? (
                    <Typography
                      variant="caption"
                      sx={{ color: 'warning.dark', fontStyle: 'italic' }}
                    >{translateKey("hardcoded.placowka_jest_w_trakcie_weryfikacji")}</Typography>
                  ) : (
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={0.5}
                    >
                      {/* Role Selection Chips */}
                      {availableRoles.length > 0 && (
                        <Stack direction="row" spacing={0.5}>
                          {availableRoles.map((role) => {
                            const isSelectedRole =
                              selectedOrganization?.role === role;
                            const isActive = isSelectedRole && isSelected;

                            return role === Roles.organization_owner ? null : (
                              <Chip
                                key={role}
                                label={getRoleDisplayName(role)}
                                size="small"
                                variant={isActive ? 'filled' : 'outlined'}
                                color={isActive ? 'primary' : 'default'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectRole(organization, role);
                                }}
                                sx={{
                                  fontSize: '0.75rem',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    bgcolor: isActive
                                      ? 'primary.dark'
                                      : 'transparent',
                                  },
                                }}
                              />
                            );
                          })}
                          {hasOrganizationOwnerRole && (
                            <Tooltip
                              title={translateKey("hardcoded.jestes_wlascicielem_tej_placowki")}
                              placement="top"
                              arrow
                            >
                              <Iconify
                                icon="ic:baseline-business-center"
                                sx={{ color: 'primary.main' }}
                                width={16}
                                height={16}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      )}
                      <Tooltip
                        title={translateKey("hardcoded.ustawienia_placowki")}
                        placement="top"
                        arrow
                      >
                        <IconButton
                          onClick={() =>
                            handleGoToOrganizationSettings(organization)
                          }
                        >
                          <Iconify
                            icon="ic:settings"
                            width={15}
                            height={15}
                            sx={{ marginRight: '0 !important' }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Stack>
              </MenuItem>
            );
          })}
        </Stack>
      </MenuPopover>
    </>
  );
}
